import React, { useState, useEffect } from 'react'
import { X, Calendar, Clock, Building, ChevronDown } from 'lucide-react'
import { employeeShiftService } from '../../services/employeeShiftService'
import InlineLoader from '../ui/InlineLoader'

const EditEmployeeShiftModal = ({ employeeShift, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    status: '',
    endDate: '',
    isActive: true
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Initialize form data
  useEffect(() => {
    if (employeeShift) {
      setFormData({
        status: employeeShift.status || '',
        endDate: employeeShift.endDate ? employeeShift.endDate.split('T')[0] : '',
        isActive: employeeShift.isActive !== undefined ? employeeShift.isActive : true
      })
    }
  }, [employeeShift])

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.status) {
      newErrors.status = 'Status is required'
    }

    // Validate date logic
    if (formData.endDate && employeeShift.startDate) {
      const startDate = new Date(employeeShift.startDate)
      const endDate = new Date(formData.endDate)
      
      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)

      // Only include fields that have changed
      const updateData = {}

      // Check if status changed
      if (formData.status !== employeeShift.status) {
        updateData.status = formData.status
      }

      // Check if isActive changed
      if (formData.isActive !== employeeShift.isActive) {
        updateData.isActive = formData.isActive
      }

      // Check if endDate changed
      const originalEndDate = employeeShift.endDate ? employeeShift.endDate.split('T')[0] : ''
      if (formData.endDate !== originalEndDate) {
        if (formData.endDate) {
          updateData.endDate = formData.endDate
        } else {
          updateData.endDate = null // Clear end date if empty
        }
      }

      // Only proceed if there are changes
      if (Object.keys(updateData).length === 0) {
        setErrors({
          submit: 'No changes detected'
        })
        setLoading(false)
        return
      }

      await employeeShiftService.updateEmployeeShift(employeeShift._id, updateData)
      onSuccess()
    } catch (error) {
      console.error('Error updating employee shift:', error)
      setErrors({
        submit: error.response?.data?.message || 'Failed to update employee shift'
      })
    } finally {
      setLoading(false)
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Edit Employee Shift</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Employee and Shift Info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="space-y-2">
            <div className="flex items-center">
              <Clock className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">Employee:</span>
              <span className="text-sm font-medium text-gray-900 ml-2">
                {employeeShift.employee?.fullName || 'N/A'}
              </span>
            </div>
            <div className="flex items-center">
              <Building className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">Shift:</span>
              <span className="text-sm font-medium text-gray-900 ml-2">
                {employeeShift.shift?.shiftName || 'N/A'}
              </span>
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">Start Date:</span>
              <span className="text-sm font-medium text-gray-900 ml-2">
                {formatDate(employeeShift.startDate)}
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Status */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status *
            </label>
            <div className="relative">
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={`appearance-none w-full px-3 py-2 pr-8 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white ${
                  errors.status ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Status</option>
                <option value="pending">Pending</option>
                <option value="open">Open</option>
                <option value="closed">Close</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
            </div>
            {errors.status && (
              <p className="text-red-500 text-sm mt-1">{errors.status}</p>
            )}
          </div>

          {/* End Date */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date (Optional)
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`pl-10 pr-3 py-2 w-full border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.endDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.endDate && (
              <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
            )}
          </div>

          {/* Active Status */}
          <div className="mb-6">
            <label className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Status</span>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                  formData.isActive ? 'bg-orange-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && <InlineLoader size="sm" />}
              <span>Update Shift</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditEmployeeShiftModal
